'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';


interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'client' | 'admin' | 'manager' | 'super_admin';
  phone?: string;
  account_number?: string;
  account_type?: string;
  balance?: number;
  equity?: number;
  leverage?: string;
  account_status?: string;
  kyc?: string;
  is_active?: boolean;
  avatar_url?: string;
  profile_picture?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  role: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isUser: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (error) return null;
      return data as UserProfile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        let p = await fetchProfile(session.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        let p = await fetchProfile(session.user.id);
        setProfile(p);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Real-time subscription: keep profile balance/equity in sync
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`auth_profile_realtime_${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_profiles',
        filter: `id=eq.${user.id}`,
      }, (payload) => {
        setProfile(prev => prev ? { ...prev, ...payload.new } as UserProfile : prev);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'client'
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data.session) {
      throw new Error('Sign-in succeeded but no session was returned. Please try again.');
    }

    // Prefer the session returned by signIn (Rocket may delay cookie reads).
    // Re-apply setSession so our partitioned-cookie / localStorage adapter persists it.
    await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });

    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData.session ?? data.session;

    setSession(session);
    setUser(session.user ?? data.user ?? null);

    let p: UserProfile | null = null;
    const uid = session.user?.id ?? data.user?.id;
    if (uid) {
      p = await fetchProfile(uid);
      const metaRole = session.user?.user_metadata?.role ?? data.user?.user_metadata?.role;
      if (!p && metaRole) {
        p = {
          id: uid,
          email: session.user?.email ?? data.user?.email ?? '',
          full_name: session.user?.user_metadata?.full_name || data.user?.user_metadata?.full_name || '',
          role: metaRole,
        };
      }
      setProfile(p);
    }

    return { ...data, profile: p, session };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    setUser(null);
    setSession(null);
    setProfile(null);
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) {
      let p = await fetchProfile(user.id);
      setProfile(p);
    }
  };

  const role = profile?.role ?? null;
  const isAdmin = role === 'admin' || role === 'manager' || role === 'super_admin';
  const isSuperAdmin = role === 'super_admin';
  const isUser = role === 'client';

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    role,
    isAdmin,
    isSuperAdmin,
    isUser,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
